import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || '';

function DeviceRegistration({ user }) {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [macAddress, setMacAddress] = useState('');
  const [deviceName, setDeviceName] = useState('iClean Reader');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [registering, setRegistering] = useState(false);

  // Decode token and fetch companies on mount
  useEffect(() => {
    async function init() {
      try {
        console.log('DeviceRegistration: Starting init with token:', token);
        console.log('DeviceRegistration: API_BASE:', API_BASE);
        
        // Decode token to get MAC address
        const decodeRes = await fetch(`${API_BASE}/api/device/decode/${token}`, {
          credentials: 'include'
        });
        
        console.log('DeviceRegistration: Decode response status:', decodeRes.status);
        
        if (!decodeRes.ok) {
          throw new Error('Érvénytelen vagy lejárt QR kód');
        }
        
        const decodeData = await decodeRes.json();
        console.log('DeviceRegistration: Decode data:', decodeData);
        setMacAddress(decodeData.mac_address);
        
        // Use user data from props (passed from App.js)
        console.log('DeviceRegistration: Using user from props:', user);
        
        if (!user || !user.companies) {
          console.error('No user or companies data:', user);
          setError('Nincs bejelentkezve vagy nincs jogosultság');
          setLoading(false);
          return;
        }
        
        setCompanies(user.companies || []);
        
        if (user.companies && user.companies.length > 0) {
          setSelectedCompany(user.companies[0].id || user.companies[0].company_id);
        }
        
        console.log('DeviceRegistration: Setting loading to false');
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    }
    
    init();
  }, [token]);

  const handleRegister = async () => {
    if (!selectedCompany) {
      setError('Válassz céget!');
      return;
    }
    
    setRegistering(true);
    setError('');
    
    try {
      const requestData = {
        token: token,
        company_id: parseInt(selectedCompany),
        device_name: deviceName
      };
      
      console.log('DeviceRegistration: Sending request data:', requestData);
      console.log('DeviceRegistration: Token:', token);
      console.log('DeviceRegistration: Selected company:', selectedCompany);
      console.log('DeviceRegistration: Parsed company_id:', parseInt(selectedCompany));
      
      const res = await fetch(`${API_BASE}/api/device/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(requestData)
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Regisztráció sikertelen');
      }
      
      setSuccess(data.message || 'Eszköz sikeresen regisztrálva!');
      
      // Redirect to home after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Token dekódolása...</p>
        </div>
      </div>
    );
  }

  if (error && !macAddress) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold mb-4">Hiba történt</h2>
          <p className="text-gray-700 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600"
          >
            Vissza a főoldalra
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
        <h1 className="text-2xl font-bold mb-6 text-center">iClean Reader Regisztráció</h1>
        
        {success ? (
          <div className="text-center">
            <div className="text-green-500 text-5xl mb-4">✓</div>
            <p className="text-green-600 font-medium mb-4">{success}</p>
            <p className="text-gray-600 text-sm">Átirányítás...</p>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-2">
                Eszköz MAC címe:
              </label>
              <div className="bg-gray-100 p-3 rounded font-mono text-sm">
                {macAddress}
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-2">
                Eszköz neve:
              </label>
              <input
                type="text"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="pl. Reader #1"
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-2">
                Cég kiválasztása:
              </label>
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Válassz céget --</option>
                {companies.map((company) => (
                  <option key={company.id || company.company_id} value={company.id || company.company_id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
            
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
                {error}
              </div>
            )}
            
            <button
              onClick={handleRegister}
              disabled={registering || !selectedCompany}
              className="w-full bg-blue-500 text-white py-3 px-4 rounded font-medium hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {registering ? 'Regisztrálás...' : 'Eszköz regisztrálása'}
            </button>
            
            <button
              onClick={() => navigate('/')}
              className="w-full mt-3 bg-gray-200 text-gray-700 py-2 px-4 rounded hover:bg-gray-300 transition-colors"
            >
              Mégse
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default DeviceRegistration;

